<template>
  <div
    class="bg-box w-screen h-screen overflow-hidden relative"
    :style="containerStyle"
    :class="{ 'bg-cover bg-center': isPreset || isUserCustom }"
  >
    <!-- 组件背景 -->
    <component
      :is="activeBgComponent"
      v-if="isComponentBg && activeBgComponent"
      v-bind="activeBgProps"
      class="fixed inset-0 z-0"
    />

    <div
      :id="APP_SCALE_ROOT_ID"
      class="flex flex-col w-full h-full relative z-10"
      :style="contentStyle"
    >
      <ConfigProvider :teleport-to="scaleRootSelector">
        <header class="h-21 w-full shrink-0 relative z-10">
          <Header />
        </header>

        <main id="main-content" class="flex-1 w-full min-h-0 overflow-y-auto relative z-10">
          <RouterView v-slot="{ Component, route }">
            <transition
              name="fade"
              mode="out-in"
              enter-active-class="animate-in fade-in duration-150"
              leave-active-class="animate-out fade-out duration-150"
            >
              <component :is="Component" :key="route.meta.parentKey || route.path" />
            </transition>
          </RouterView>
        </main>

        <FirmwareUpgradeProgressDialog />
      </ConfigProvider>
      <Toaster position="top-center" />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { defineAsyncComponent, computed, onUnmounted, shallowRef, watch } from 'vue'
  import { storeToRefs } from 'pinia'
  import { ConfigProvider, Toaster } from '@rapoo/element'
  import { getDefaultWallpaperByTheme, useWallpaperStore, Wallpaper } from '@/stores/wallpaper'
  import { useThemeStore } from '@/stores/theme'
  import { getImageUrl } from '@/utils/assets'
  import { BackgroundComponents } from '@/components/backgrounds'
  import { APP_SCALE_ROOT_ID, useViewportScale } from '@/composables/useZoomScale'
  import FirmwareUpgradeProgressDialog from '@/components/firmware/FirmwareUpgradeProgressDialog.vue'

  // Header 含大量 @rapoo/element 组件，异步加载避免挡住首页首屏
  const Header = defineAsyncComponent(() => import('@/views/header/index.vue'))

  const { scale, viewportWidth, viewportHeight } = useViewportScale()
  const scaleRootSelector = `#${APP_SCALE_ROOT_ID}`

  const contentStyle = computed(() => {
    if (scale.value >= 1) {
      return {}
    }

    return {
      width: `${viewportWidth.value / scale.value}px`,
      height: `${viewportHeight.value / scale.value}px`,
      transformOrigin: 'top left',
      transform: `scale(${scale.value})`,
    }
  })

  const themeStore = useThemeStore()
  const { isDark } = storeToRefs(themeStore)

  const wallpaperStore = useWallpaperStore()
  wallpaperStore.initDefaultWallpaper(isDark.value)
  const { currentWallpaper, isPreset, isUserCustom, isComponentBg, customConfig, pageOverride } =
    storeToRefs(wallpaperStore)

  const presetWallpaperIds = new Set<string>(Object.values(Wallpaper))
  const activeUserCustomUrl = shallowRef('')
  let activeUserCustomId = ''

  const containerStyle = computed(() => {
    if (isUserCustom.value && activeUserCustomUrl.value) {
      return {
        backgroundImage: `url(${activeUserCustomUrl.value})`,
      }
    }

    if (isPreset.value) {
      const wallpaper = presetWallpaperIds.has(currentWallpaper.value)
        ? currentWallpaper.value
        : getDefaultWallpaperByTheme(isDark.value)

      const url = wallpaper.startsWith('blob:')
        ? wallpaper
        : getImageUrl(`home/homeBg/${wallpaper}.webp`)

      return {
        backgroundImage: `url(${url})`,
      }
    }

    if (pageOverride.value) return {}

    const config = customConfig.value
    if (!config) return {}

    if (config.type === 'solid' && config.color) {
      return { backgroundColor: config.color }
    }
    if (config.type === 'gradient' && config.gradient) {
      return { backgroundImage: config.gradient }
    }

    return {}
  })

  watch(
    () => [currentWallpaper.value, isPreset.value, isUserCustom.value] as const,
    async ([id, selectedPreset, selectedUserCustom]) => {
      const shouldLoadUserCustom =
        selectedUserCustom || (selectedPreset && !presetWallpaperIds.has(id))

      if (!shouldLoadUserCustom) {
        if (activeUserCustomUrl.value) {
          URL.revokeObjectURL(activeUserCustomUrl.value)
          activeUserCustomUrl.value = ''
          activeUserCustomId = ''
        }
        return
      }
      if (activeUserCustomId === id && activeUserCustomUrl.value) return

      // IndexedDB 仅用户自定义壁纸需要，延后加载避免拖慢壳层模块图
      const { getWallpaper } = await import('@/views/header/components/settings/main/theme/db')
      const wallpaper = await getWallpaper(id)
      if (!wallpaper || currentWallpaper.value !== id) return

      const url = URL.createObjectURL(wallpaper.blob)
      if (activeUserCustomUrl.value) URL.revokeObjectURL(activeUserCustomUrl.value)
      activeUserCustomUrl.value = url
      activeUserCustomId = id

      wallpaperStore.selectWallpaper({
        category: 'userCustom',
        wallpaper: {
          createdAt: wallpaper.createdAt,
          id: wallpaper.id,
          mimeType: wallpaper.mimeType,
          theme: wallpaper.theme,
          size: wallpaper.size,
          order: wallpaper.order,
          type: 'userCustom',
          url,
        },
      })
    },
    { immediate: true },
  )

  onUnmounted(() => {
    if (activeUserCustomUrl.value) URL.revokeObjectURL(activeUserCustomUrl.value)
  })

  // 解析当前组件背景（优先使用页面级覆盖）
  const activeBgComponent = computed(() => {
    if (pageOverride.value?.component) {
      return BackgroundComponents[pageOverride.value.component] || null
    }
    if (!isComponentBg.value || !customConfig.value?.component) return null
    return BackgroundComponents[customConfig.value.component] || null
  })

  // 背景 props
  const activeBgProps = computed(() => {
    if (pageOverride.value?.props) {
      return pageOverride.value.props
    }
    return {}
  })
</script>

<style scoped>
  .bg-box {
    transition: background-image 0.5s;
  }
</style>
